import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { unifiedSports } from "@/lib/sports/unifiedSportsService";

export type MatchStatsPayload = Record<string, number | string>;

export interface UseMatchInsightsResult {
  stats: MatchStatsPayload | undefined;
  isLoading: boolean;
  isError: boolean;
  isDemo: boolean;
  updatedAt: number | undefined;
}

/**
 * Real Data Phase 1 — isolated hook for the active match only.
 *
 * Fetches stats via `unifiedSports.getStats(fixtureId)` for a single fixture.
 * Intentionally NOT integrated into `useMatchHub` to keep cross-match toast
 * effects free of stats data in their dependency arrays.
 */
export function useMatchInsights(
  fixtureId: string | undefined,
): UseMatchInsightsResult {
  const enabled = typeof fixtureId === "string" && fixtureId.length > 0;

  const query = useQuery<MatchStatsPayload>({
    queryKey: ["match-insights", "stats", fixtureId ?? "__none__"],
    queryFn: () => unifiedSports.getStats(fixtureId as string),
    enabled,
    refetchInterval: 60_000,
    staleTime: 45_000,
    retry: 1,
  });

  return {
    stats: query.data,
    isLoading: enabled && query.isLoading,
    isError: query.isError,
    isDemo:
      (query.data as { __source?: string } | undefined)?.__source === "demo"
      || unifiedSports.getActiveProvider() === "demo",
    updatedAt: query.dataUpdatedAt || undefined,
  };
}

/**
 * Reads the `real_match_details_enabled` feature flag from the backend
 * `feature_flags` table. Defaults to `false` if the row is missing or the
 * read fails. Cached for 5 minutes to avoid hammering the DB.
 */
export function useRealMatchDetailsEnabled(): boolean {
  const { data } = useQuery<boolean>({
    queryKey: ["feature-flag", "real_match_details_enabled"],
    queryFn: async () => {
      try {
        const { data: row } = await supabase
          .from("feature_flags")
          .select("enabled")
          .eq("key", "real_match_details_enabled")
          .maybeSingle();
        return !!row?.enabled;
      } catch {
        return false;
      }
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: 0,
  });
  return data ?? false;
}